import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  Autocomplete,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Link as MuiLink,
  Tabs,
  Tab,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import Header from '../Header';
import Footer from '../Footer';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import config from "../common/Config";
import * as XLSX from 'xlsx';
import "../common/commonStyles.css";
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const SalesOrderReport = () => {
  const [reportrange, setReportrange] = useState('');
  const [dates, setDates] = useState([null, null]);
  const [reportDate, setReportDate] = useState('');
  const [salesRapOptions, setSalesRapOptions] = useState([]);
  const [salesRap, setSalesRap] = useState('');
  const [routeOptions, setRouteOptions] = useState([]);
  const [salesRoute, setSalesRoute] = useState('');
  const [salesOrderReport, setSalesOrderReport] = useState([]);
  const [totals, setTotals] = useState({
    netAmount: 0,
    taxTotal: 0,
    grandTotal: 0,
    paidTotal: 0
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedSalesRep, setSelectedSalesRep] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [activeReportTab, setActiveReportTab] = useState('invoice');
  const [salesVatReport, setSalesVatReport] = useState([]);
  const [vatTotalCount, setVatTotalCount] = useState(0);
  const [vatTotals, setVatTotals] = useState({ netAmount: 0, vatAmount: 0, grossAmount: 0 });
  const [appliedSalesRap, setAppliedSalesRap] = useState('');
  const [appliedRoute, setAppliedRoute] = useState('');
  const [submitKey, setSubmitKey] = useState(0);
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');
  const currencySymbol = localStorage.getItem("currencySymbol") || '';
  const company_tax_type = localStorage.getItem("company_tax_type") || 'Tax';

  const handleDateChange = (value) => {
    setDates(value);
  };

  const fetchSalesRapOptions = async () => {
    const apiSalesRapUrl = `${config.API_URL}/getSalesRep`;
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const body = new FormData();
      if (useEncrypt) {
        const ep = encryptPayload({});
        if (ep) {
          body.append('encrypted_payload', ep);
        }
      }
      const response = await fetch(apiSalesRapUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: body.entries().next().done ? undefined : body
      });
      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      const formattedOptions = (data.data || []).map(item => ({
        label: `${item.first_name} ${item.last_name}`,
        value: item.id,
      }));
      setSalesRapOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchSalesRoute = async () => {
    const apiSalesRouteUrl = `${config.API_URL}/getRoute`;
    try {
      const response = await fetch(apiSalesRouteUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        }
      });
      const data = await response.json();
      const formattedOptions = (data.data || []).map(item => ({
        label: `${item.route_number}`,
        value: item.id,
      }));
      setRouteOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchReport = useCallback(async () => {
    if (submitKey === 0) return;

    setLoading(true);
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const formData = new FormData();
      const plain = {
        draw: 1,
        start: page * rowsPerPage,
        length: rowsPerPage,
        searchValue: appliedSearch,
        reportrange,
        sales_rep: appliedSalesRap || '',
        route_id: appliedRoute || ''
      };
      if (useEncrypt) {
        const ep = encryptPayload(plain);
        if (ep) {
          formData.append('encrypted_payload', ep);
        } else {
          Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
        }
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }

      const response = await fetch(`${config.API_URL}/ws-SalesOrder-report`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to fetch report');

      const raw = await response.json();
      const json = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;

      if (json && json.data && Array.isArray(json.data)) {
        setSalesOrderReport(json.data);
        setTotalCount(json.recordsTotal || json.data.length);
        if (json.startdate && json.enddate) {
          setReportDate(json.startdate + ' To Date : ' + json.enddate);
        }

        const netAmount = parseFloat(json.sum_total_net || 0);
        const taxTotal = parseFloat(json.sum_total_tax || 0);
        const grandTotal = parseFloat(json.sum_grand_total || 0);
        const paidTotal = parseFloat(json.sum_total_paid || 0);

        setTotals({ netAmount, taxTotal, grandTotal, paidTotal });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [accessToken, token, page, rowsPerPage, reportrange, appliedSalesRap, appliedRoute, appliedSearch, submitKey]);

  const fetchVatReport = useCallback(async () => {
    if (submitKey === 0) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('draw', 1);
      formData.append('start', page * rowsPerPage);
      formData.append('length', rowsPerPage);
      formData.append('searchValue', appliedSearch);
      formData.append('reportrange', reportrange);
      formData.append('sales_rep', appliedSalesRap || '');
      formData.append('route_id', appliedRoute || '');

      const response = await fetch(`${config.API_URL}/ws-SalesVatWise-report`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to fetch report');

      const json = await response.json();

      if (json && json.data && Array.isArray(json.data)) {
        setSalesVatReport(json.data);
        setVatTotalCount(json.recordsTotal || json.data.length);
        if (json.startdate && json.enddate) {
          setReportDate(json.startdate + ' To Date : ' + json.enddate);
        }
        const sumFromApi = (keys, field) => {
          for (const k of keys) {
            if (json[k] !== undefined && json[k] !== null && json[k] !== '') {
              const v = parseFloat(json[k]);
              if (!Number.isNaN(v)) return v;
            }
          }
          return json.data.reduce((s, r) => s + parseFloat(r[field] || 0), 0);
        };
        setVatTotals({
          netAmount: sumFromApi(['sum_net_amount', 'sum_net'], 'net_amount'),
          vatAmount: sumFromApi(['sum_vat_amount', 'sum_vat'], 'vat_amount'),
          grossAmount: sumFromApi(['sum_gross_amount', 'sum_gross'], 'gross_amount'),
        });
      } else {
        setSalesVatReport([]);
        setVatTotalCount(0);
        setVatTotals({ netAmount: 0, vatAmount: 0, grossAmount: 0 });
      }
    } catch (error) {
      setSalesVatReport([]);
      setVatTotalCount(0);
      setVatTotals({ netAmount: 0, vatAmount: 0, grossAmount: 0 });
    } finally {
      setLoading(false);
    }
  }, [accessToken, token, page, rowsPerPage, reportrange, appliedSalesRap, appliedRoute, appliedSearch, submitKey]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchSalesRapOptions();
    fetchSalesRoute();
  }, []);

  useEffect(() => {
    if (submitKey === 0) return;
    if (activeReportTab === 'invoice') {
      fetchReport();
    } else {
      fetchVatReport();
    }
  }, [submitKey, reportrange, page, rowsPerPage, appliedSalesRap, appliedRoute, appliedSearch, activeReportTab, fetchReport, fetchVatReport]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSalesRepChange = (event, newValue) => {
    setSelectedSalesRep(newValue);
    setSalesRap(newValue ? newValue.value : '');
  };

  const handleRouteChange = (event, newValue) => {
    setSelectedRoute(newValue);
    setSalesRoute(newValue ? newValue.value : '');
  };

  const handleReportTabChange = (event, newValue) => {
    setActiveReportTab(newValue);
    setPage(0);
  };

  const searchFormData = () => {
    let range = '';
    let reportDateLabel = '';
    if (dates[0] && dates[1]) {
      const startDate = format(dates[0], 'dd/MM/yyyy');
      const endDate = format(dates[1], 'dd/MM/yyyy');
      range = `${startDate} - ${endDate}`;
      reportDateLabel = `${startDate} To Date : ${endDate}`;
    }
    setReportrange(range);
    setReportDate(reportDateLabel);
    setAppliedSearch(searchQuery);
    setAppliedSalesRap(salesRap || '');
    setAppliedRoute(salesRoute || '');
    setPage(0);
    setSubmitKey((key) => key + 1);
  };

  const vatRowCustomerName = (row) => row.company || row.CustomerName || row.customer_name || row.customer || '';
  const vatRowInvoiceNo = (row) => row.reference_no || row.InvoiceNumber || row.invoice_number || row.order_ref || '';
  const vatRowInvoiceDate = (row) => row.date || row.InvoiceDate || row.invoice_date || '';
  const vatRowAccountCode = (row) => row.account_no || row.AccountCode || row.account_code || row.accound_no || '';
  const vatRowTaxRate = (row) => (row.tax_rate != null && row.tax_rate !== '' ? row.tax_rate : '') || row.TaxRate || '';
  const vatRowNetAmount = (row) => row.net_amount;
  const vatRowVatAmount = (row) => row.vat_amount;
  const vatRowGrossAmount = (row) => row.gross_amount;

  const exportToExcel = async () => {
    const settings = await getCompanySettings();
    const useEncrypt = resolveCustomerApiEncrypt(settings);
    const formData = new FormData();
    const countForExport = activeReportTab === 'invoice' ? (totalCount || 10000) : (vatTotalCount || 10000);
    const plain = {
      draw: '1',
      start: '0',
      length: countForExport,
      searchValue: appliedSearch,
      reportrange,
      sales_rep: appliedSalesRap || '',
      route_id: appliedRoute || ''
    };
    if (useEncrypt) {
      const ep = encryptPayload(plain);
      if (ep) {
        formData.append('encrypted_payload', ep);
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }
    } else {
      Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
    }

    const apiActivePdtUrl = activeReportTab === 'invoice'
      ? `${config.API_URL}/ws-SalesOrder-report`
      : `${config.API_URL}/ws-SalesVatWise-report`;

    try {
      const response = await fetch(apiActivePdtUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;

      if (activeReportTab === 'invoice') {
        const exportData = (data.data || []).map(product => ({
          Date: product.date || '',
          "Customer (A/C)": product.company
            ? `${product.company} (${product.accound_no || ''})`
            : product.accound_no || '',
          "Order Number": product.reference_no || '',
          "Net Amount": parseFloat(product.net_amount || 0),
          [`${company_tax_type} Total`]: parseFloat(product.total_tax || 0),
          "Grand Total": parseFloat(product.grand_total || 0),
          "Paid Total": parseFloat(product.paid || 0)
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const colWidths = [
          { wch: 12 },
          { wch: 25 },
          { wch: 15 },
          { wch: 12 },
          { wch: 12 },
          { wch: 12 },
          { wch: 12 },
        ];
        ws['!cols'] = colWidths;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sales Order Report');

        const now = new Date();
        const formattedDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const formattedTime = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
        const filename = `Sales-Order-Report-${formattedDate}-${formattedTime}.xlsx`;

        XLSX.writeFile(wb, filename);
      } else {
        const exportData = (data.data || []).map((row) => ({
          CustomerName: vatRowCustomerName(row),
          InvoiceNumber: vatRowInvoiceNo(row),
          InvoiceDate: vatRowInvoiceDate(row),
          AccountCode: vatRowAccountCode(row),
          TaxRate: vatRowTaxRate(row) === '' ? '' : parseFloat(vatRowTaxRate(row)),
          NetAmount: parseFloat(row.net_amount || 0),
          VATAmount: parseFloat(row.vat_amount || 0),
          GrossAmount: parseFloat(row.gross_amount || 0),
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        ws['!cols'] = [
          { wch: 28 },
          { wch: 14 },
          { wch: 12 },
          { wch: 12 },
          { wch: 12 },
          { wch: 12 },
          { wch: 12 },
          { wch: 12 },
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'VAT Wise');

        const now = new Date();
        const formattedDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const formattedTime = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
        const filename = `Sales-Vat-Wise-Report-${formattedDate}-${formattedTime}.xlsx`;

        XLSX.writeFile(wb, filename);
      }
    } catch (error) {
    }
  };

  const formatNumber = (num) => {
    return parseFloat(num || 0).toFixed(2);
  };

  return (
    <>
      {/* <Header /> */}
      <Box className="page-container">
          <Paper className="paper-style"
          >
            <Box sx={{ px: 2, py: 1.2 }}>
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, mb:1, gap: 2 }}>
                <Typography className="page-title">
                  Sales Order Report
                </Typography>
              </Box>
              <Tabs
              className="common-tabs"
                value={activeReportTab}
                onChange={handleReportTabChange}
                sx={{ px: 2, borderBottom: 1, borderColor: "divider", mb: 1 }}
              >
                <Tab label="Invoice wise" value="invoice" />
                <Tab label="VAT wise" value="vat" />
              </Tabs>

              <Grid2 container spacing={2} >
              <Grid2 size={{ xs: 12, md: 3 }}>
                  <TextField
                    className="input-border"
                    fullWidth
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                    }}
                    size="small"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Iconify icon="eva:search-outline" width={20} sx={{ color: "#637381" }} />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSearchQuery('');
                              }}
                              sx={{ color: "#637381" }}
                            >
                              <Iconify icon="eva:close-fill" width={20} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f5f7fa",
                        fontSize: "0.875rem",
                        "&:hover": {
                          backgroundColor: "#fff",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#fff",
                        },
                      },
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 3 }}>
                  <CommonDatePicker
                    className="input-border"
                    value={dates}
                    onChange={handleDateChange}
                    format="dd/MM/yyyy"
                    character=" - "
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 2}}>
                  <Autocomplete
                    className="input-border"
                    options={salesRapOptions}
                    value={selectedSalesRep}
                    onChange={handleSalesRepChange}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Sales Rep"
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#f5f7fa",
                            fontSize: "0.875rem",
                            "&:hover": {
                              backgroundColor: "#fff",
                            },
                            "&.Mui-focused": {
                              backgroundColor: "#fff",
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md:2}}>
                  <Autocomplete
                    options={routeOptions}
                    value={selectedRoute}
                    onChange={handleRouteChange}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        className="input-border"
                        {...params}
                        placeholder="Select Route"
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#f5f7fa",
                            fontSize: "0.875rem",
                            "&:hover": {
                              backgroundColor: "#fff",
                            },
                            "&.Mui-focused": {
                              backgroundColor: "#fff",
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Grid2>
      
                <Grid2 size={{ xs: 12, md:2}} display="flex" alignItems="flex-end">
                <Stack direction="row" spacing={1} width="100%">
                  <Button
                    variant="contained"
                    onClick={searchFormData}
                    fullWidth
                    className="button-variant-contained"
                  >
                    Submit
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={exportToExcel}
                    fullWidth
                    className="button-variant-outlined"
                    startIcon={<Iconify icon="eva:download-outline" width={20} />}
                  >
                    Export
                  </Button>
                  </Stack>
                </Grid2>
              </Grid2>
              </Box>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={40} />
                </Box>
              ) : activeReportTab === 'invoice' ? (
                <TableContainer >
                  <Table size="medium" className="table-cell-border" sx={{ tableLayout: "fixed" }}>
                    <TableHead>
                      <TableRow className="table-cell-header">
                        <TableCell
                          width="60px"
                        >
                          Sr.no
                        </TableCell>
                        <TableCell
                          width="100px"
                        >
                          Date
                        </TableCell>
                        <TableCell
                          width="350px"
                        >
                          Customer (A/C)
                        </TableCell>
                        <TableCell
                          width="150px"
                        >
                          Order. Ref
                        </TableCell>
                        <TableCell
                          width="120px"
                          align="right"
                        >
                          Net. Amount
                        </TableCell>
                        <TableCell
                          sx={{
          
                            width: "120px",
                            minWidth: "120px",
                            textAlign: "right",
                          }}
                        >
                          {company_tax_type}. Total
                        </TableCell>
                        <TableCell
                          sx={{
          
                            width: "120px",
                            minWidth: "120px",
                            textAlign: "right",
                          }}
                        >
                          Gross. Total
                        </TableCell>
                        <TableCell
                          sx={{
      
                            width: "120px",
                            minWidth: "120px",
                            textAlign: "right",
                          }}
                        >
                          Paid Total
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {salesOrderReport.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" sx={{ color: "#637381" }}>
                              {submitKey > 0 ? "No data available" : "Please select filters and click Submit"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        salesOrderReport.map((row, index) => (
                          <TableRow
                            key={index}
                            className="table-cell-style"
                            sx={{
                              "&:last-child td": {
                                borderBottom: "none",
                              },
                          
                            }}
                          >
                            <TableCell
                            >
                              {index + 1 + page * rowsPerPage}
                            </TableCell>
                            <TableCell
                            >
                              {row.date || ""}
                            </TableCell>
                            <TableCell
                            >
                              {row.company ? `${row.company} (${row.accound_no || ''})` : ""}
                            </TableCell>
                            <TableCell
                            >
                              <MuiLink
                                href={`${config.BASE_URL}/reports/mis_report/print/${row.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: "#212b36", textDecoration: "underline !important", "&:hover": { textDecoration: "underline !important" } }}
                              >
                                {row.reference_no || ""}
                              </MuiLink>
                            </TableCell>
                            <TableCell
                              sx={{
                                textAlign: "right",
                              }}
                            >
                              {currencySymbol} {formatNumber(row.net_amount)}
                            </TableCell>
                            <TableCell
                              sx={{
                                textAlign: "right",
                              }}
                            >
                              {currencySymbol} {formatNumber(row.total_tax)}
                            </TableCell>
                            <TableCell
                              sx={{
                                textAlign: "right",
                              }}
                            >
                              {currencySymbol} {formatNumber(row.grand_total)}
                            </TableCell>
                            <TableCell
                              sx={{
                                textAlign: "right",
                              }}
                            >
                              {currencySymbol} {formatNumber(row.paid)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                    <TableFooter>
                      <TableRow 
                         sx={{
                          "& .MuiTableCell-root": {
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "#212b36",
                            py: 1.2,
                            textAlign: "right",
                            backgroundColor: "#f5f7fa",
                            borderBottom: "none",
                          },
                         }}
                       >
                        <TableCell colSpan={4} sx={{ borderBottom: "none" }} />
                        <TableCell
                  
                        >
                          {currencySymbol} {formatNumber(totals.netAmount)}
                        </TableCell>
                        <TableCell
                 
                        >
                          {currencySymbol} {formatNumber(totals.taxTotal)}
                        </TableCell>
                        <TableCell
                   
                        >
                          {currencySymbol} {formatNumber(totals.grandTotal)}
                        </TableCell>
                        <TableCell
                       
                        >
                          {currencySymbol} {formatNumber(totals.paidTotal)}
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              ) : (
                <TableContainer>
                  <Table size="medium" className="table-cell-border" sx={{ tableLayout: "fixed" }}>
                    <TableHead>
                      <TableRow className="table-cell-header">
                        <TableCell width="52px">Sr.no</TableCell>
                        <TableCell width="180px">Customer name</TableCell>
                        <TableCell width="110px">Invoice number</TableCell>
                        <TableCell width="92px">Invoice date</TableCell>
                        <TableCell width="100px">Account code</TableCell>
                        <TableCell width="88px" align="right">Tax rate %</TableCell>
                        <TableCell width="100px" align="right">Net amount</TableCell>
                        <TableCell width="100px" align="right">VAT amount</TableCell>
                        <TableCell width="100px" align="right">Gross amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {salesVatReport.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" sx={{ color: "#637381" }}>
                              {submitKey > 0 ? "No data available" : "Please select filters and click Submit"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        salesVatReport.map((row, index) => {
                          const printId = row.id || row.sales_order_id || row.invoice_id;
                          const invNo = vatRowInvoiceNo(row);
                          const rateVal = vatRowTaxRate(row);
                          return (
                            <TableRow
                              key={index}
                              className="table-cell-style"
                              sx={{ "&:last-child td": { borderBottom: "none" } }}
                            >
                              <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
                              <TableCell>{vatRowCustomerName(row)}</TableCell>
                              <TableCell>
                                {printId ? (
                                  <MuiLink
                                    href={`${config.BASE_URL}/reports/mis_report/print/${printId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ color: "#212b36", textDecoration: "underline !important", "&:hover": { textDecoration: "underline !important" } }}
                                  >
                                    {invNo}
                                  </MuiLink>
                                ) : (
                                  invNo
                                )}
                              </TableCell>
                              <TableCell>{vatRowInvoiceDate(row)}</TableCell>
                              <TableCell>{vatRowAccountCode(row)}</TableCell>
                              <TableCell sx={{ textAlign: "right" }}>{rateVal === '' ? '' : `${formatNumber(rateVal)}%`}</TableCell>
                              <TableCell sx={{ textAlign: "right" }}>{currencySymbol} {formatNumber(vatRowNetAmount(row))}</TableCell>
                              <TableCell sx={{ textAlign: "right" }}>{currencySymbol} {formatNumber(vatRowVatAmount(row))}</TableCell>
                              <TableCell sx={{ textAlign: "right" }}>{currencySymbol} {formatNumber(vatRowGrossAmount(row))}</TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                    {salesVatReport.length > 0 && (
                      <TableFooter>
                        <TableRow
                          sx={{
                            "& .MuiTableCell-root": {
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: "#212b36",
                              py: 1.2,
                              textAlign: "right",
                              backgroundColor: "#f5f7fa",
                              borderBottom: "none",
                            },
                          }}
                        >
                          <TableCell colSpan={6} sx={{ borderBottom: "none" }} />
                          <TableCell>{currencySymbol} {formatNumber(vatTotals.netAmount)}</TableCell>
                          <TableCell>{currencySymbol} {formatNumber(vatTotals.vatAmount)}</TableCell>
                          <TableCell>{currencySymbol} {formatNumber(vatTotals.grossAmount)}</TableCell>
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </TableContainer>
              )}

              {!loading && activeReportTab === 'invoice' && salesOrderReport.length > 0 && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", borderTop: "1px solid rgba(145, 158, 171, 0.16)" }}>
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="Rows per page:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to}${count !== -1 ? ` of ${count}` : ''}`}
                    className="pagination-styles"
                  />
                </Box>
              )}
              {!loading && activeReportTab === 'vat' && salesVatReport.length > 0 && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", borderTop: "1px solid rgba(145, 158, 171, 0.16)" }}>
                  <TablePagination
                    component="div"
                    count={vatTotalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="Rows per page:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to}${count !== -1 ? ` of ${count}` : ''}`}
                    className="pagination-styles"
                  />
                </Box>
              )}
        
          </Paper>
        </Box>
    </>
  );
};

export default SalesOrderReport;
